import {
  TextField,
  TurtleTheme,
  useTurtleTheme,
  theme as originalTheme,
} from '@dmm-com/turtle-components'
import { ComponentProps, HTMLInputTypeAttribute, useMemo } from 'react'
import {
  RegisterOptions,
  UseFormRegisterReturn,
  ValidateResult,
  ValidationRule,
} from 'react-hook-form'
import { css } from '@emotion/react'

// NOTE: TextFieldのPropsはoptionalにしているため必要であればoverrideしてください
interface Props extends Partial<ComponentProps<typeof TextField>> {
  onRegister: (options: RegisterOptions) => UseFormRegisterReturn
  errorText?: string
  // override
  label: string
  placeholder: string
  required?: boolean
  requiredLabel?: string
  maxLength?: number
  minLength?: number
  pattern?: ValidationRule<RegExp>
  type?: HTMLInputTypeAttribute | undefined
  validate?: () => ValidateResult | Promise<ValidateResult>
  disabled?: boolean
  notEditableValue?: string // この値が設定された場合、背景色が変わる
}

export const CompanyTextField = ({
  onRegister,
  errorText,
  onChange,
  label,
  placeholder,
  required,
  requiredLabel,
  maxLength,
  minLength,
  pattern,
  type,
  validate,
  disabled,
  notEditableValue,
  ...rest
}: Props) => {
  const theme = useTurtleTheme()
  const styles = createStyles(theme)
  const companyTextFieldRegister = onRegister({
    minLength: minLength && {
      value: minLength,
      message: `${minLength}文字以上になるよう入力してください`,
    },
    maxLength: maxLength && {
      value: maxLength,
      message: `${maxLength}文字以内になるよう入力してください`,
    },
    pattern,
    required: required && {
      value: true,
      message: '必須です',
    },
    onChange,
    validate,
  })

  const state = useMemo(() => {
    if (disabled) {
      return 'disabled'
    }
    if (errorText) {
      return 'error'
    }
  }, [disabled, errorText])

  const requiredLabelMemo = useMemo(() => {
    if (!required) {
      return ''
    }
    return requiredLabel || '必須'
  }, [required, requiredLabel])

  const placeholderMemo = useMemo(() => {
    if (disabled && placeholder) {
      return ''
    }
    return placeholder
  }, [disabled, placeholder])

  const hasDisabledBackgroundCondition = useMemo(() => {
    return disabled && notEditableValue
  }, [disabled, notEditableValue])

  if (hasDisabledBackgroundCondition) {
    return (
      <div css={styles.wrapper} {...rest}>
        <span css={styles.label}>{label}</span>
        <p css={styles.field}>{notEditableValue}</p>
        <TextField
          inputProps={{
            name: companyTextFieldRegister.name,
            ref: companyTextFieldRegister.ref,
            onBlur: companyTextFieldRegister.onBlur,
            type,
            maxLength,
            minLength,
          }}
          placeholder={placeholderMemo}
          onChange={companyTextFieldRegister.onChange}
          css={styles.hidden}
          {...rest}
        />
      </div>
    )
  }

  return (
    <TextField
      inputProps={{
        name: companyTextFieldRegister.name,
        ref: companyTextFieldRegister.ref,
        onBlur: companyTextFieldRegister.onBlur,
        type,
        maxLength,
        minLength,
      }}
      errorText={errorText}
      state={state}
      label={label}
      placeholder={placeholderMemo}
      required={required}
      requiredLabel={requiredLabelMemo}
      onChange={companyTextFieldRegister.onChange}
      {...rest}
    />
  )
}

const createStyles = (theme: TurtleTheme) => {
  const { spacing } = theme.turtle

  return {
    hidden: css`
      display: none;
    `,
    wrapper: css`
      width: 100%;
    `,
    label: css`
      ${theme.turtle.typography.body2};
    `,
    field: css`
      ${theme.turtle.typography.body1};
      display: flex;
      align-items: center;
      padding: ${spacing.small[2]} ${spacing.small[4]};
      color: ${originalTheme.color.dark.gray[500]};
      background-color: ${originalTheme.color.dark.gray[800]};
      border-radius: ${theme.turtle.borderRadius.small};
      min-height: 40px;
      width: 100%;
    `,
  }
}
